// src/components/ui/SwiperGallery.tsx

'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, EffectFade, Scrollbar, Mousewheel } from 'swiper/modules';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowUpRight } from 'lucide-react';
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/effect-fade';

interface GalleryItem {
  id: string;
  title: string;
  date: string;
  image: string;
  link?: string;
}

interface SwiperGalleryProps {
  items: GalleryItem[];
  slidesPerView?: number;
  spaceBetween?: number;
  autoplay?: boolean;
  scrollbar?: boolean;
  pagination?: boolean;
  effect?: 'slide' | 'fade' | 'cube' | 'coverflow' | 'flip';
  className?: string;
}

/**
 * Truncates text to fit within a specified number of lines, breaking only at word boundaries
 */
function TruncatedTitle({ text, maxLines = 3, className = '' }: { text: string; maxLines?: number; className?: string }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const measureRef = useRef<HTMLHeadingElement>(null);
  const [truncatedText, setTruncatedText] = useState(text);

  const truncateText = useCallback(() => {
    if (!titleRef.current || !measureRef.current) return;

    const element = titleRef.current;
    const measureElement = measureRef.current;
    const originalText = text;
    
    // Wait for element to have width
    if (element.offsetWidth === 0) {
      // Use requestAnimationFrame to wait for layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          truncateText();
        });
      });
      return;
    }
    
    // Get the actual element's width - use getBoundingClientRect for more accuracy
    const elementRect = element.getBoundingClientRect();
    const elementWidth = elementRect.width || element.offsetWidth;
    
    if (elementWidth === 0) {
      // Element not laid out yet, retry
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          truncateText();
        });
      });
      return;
    }
    
    // Use a hidden measure element to test text height
    measureElement.textContent = originalText;
    measureElement.style.display = 'block';
    measureElement.style.visibility = 'hidden';
    measureElement.style.position = 'absolute';
    measureElement.style.top = '-9999px';
    measureElement.style.left = '-9999px';
    measureElement.style.width = elementWidth + 'px';
    measureElement.style.maxWidth = elementWidth + 'px';
    
    // Copy all relevant styles from the actual element
    const styles = window.getComputedStyle(element);
    measureElement.style.fontSize = styles.fontSize;
    measureElement.style.fontFamily = styles.fontFamily;
    measureElement.style.fontWeight = styles.fontWeight;
    measureElement.style.lineHeight = styles.lineHeight;
    measureElement.style.letterSpacing = styles.letterSpacing;
    measureElement.style.padding = styles.padding;
    measureElement.style.margin = styles.margin;
    measureElement.style.boxSizing = styles.boxSizing;
    measureElement.style.whiteSpace = 'normal';
    measureElement.style.wordBreak = 'normal';
    measureElement.style.overflowWrap = 'break-word';
    // Ensure no line-clamp or height constraints on measurement element
    measureElement.style.webkitLineClamp = 'none';
    measureElement.style.display = 'block';
    measureElement.style.webkitBoxOrient = 'unset';
    measureElement.style.overflow = 'visible';
    measureElement.style.textOverflow = 'clip';
    measureElement.style.maxHeight = 'none';
    
    // Force a reflow to ensure styles are applied
    void measureElement.offsetHeight;
    
    // Get computed line height - measure actual first line height
    const tempText = measureElement.textContent;
    measureElement.textContent = 'M'; // Single character to measure line height
    void measureElement.offsetHeight;
    const singleLineHeight = measureElement.offsetHeight;
    measureElement.textContent = tempText;
    void measureElement.offsetHeight;
    
    const lineHeight = singleLineHeight || parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;
    const maxHeight = lineHeight * maxLines;
    
    // Check if text fits within maxLines
    if (measureElement.scrollHeight <= maxHeight) {
      setTruncatedText(originalText);
      measureElement.style.display = 'none';
      return;
    }
    
    // Truncate word by word until it fits
    const words = originalText.split(' ');
    let truncated = '';
    
    for (let i = 0; i < words.length; i++) {
      const testText = i === words.length - 1 
        ? truncated + words[i]
        : truncated + words[i] + ' ';
      
      measureElement.textContent = testText;
      // Force reflow
      void measureElement.offsetHeight;
      
      if (measureElement.scrollHeight > maxHeight) {
        // This word doesn't fit, use previous version with ellipsis
        setTruncatedText(truncated.trim() + '...');
        measureElement.style.display = 'none';
        return;
      }
      
      truncated = testText;
    }
    
    setTruncatedText(truncated.trim());
    measureElement.style.display = 'none';
  }, [text, maxLines]);

  useLayoutEffect(() => {
    // Wait a bit for Swiper to finish layout
    const timeoutId = setTimeout(() => {
      truncateText();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [truncateText]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      // Debounce resize for performance
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        truncateText();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [truncateText]);

  return (
    <>
      <h3 
        ref={measureRef}
        className={`swiper-card-title-md card-title-md mb-2 ${className}`}
        aria-hidden="true"
      />
      <h3 
        ref={titleRef}
        className={`swiper-card-title-md card-title-md mb-2 ${className}`}
        style={{
          wordBreak: 'normal',
          overflowWrap: 'break-word',
          hyphens: 'none',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: maxLines,
          overflow: 'hidden',
        }}
      >
        {truncatedText}
      </h3>
    </>
  );
}

export default function SwiperGallery({
  items,
  slidesPerView = 3,
  spaceBetween = 30,
  autoplay = true,
  scrollbar = true,
  pagination = false,
  effect = 'slide',
  className = ''
}: SwiperGalleryProps) {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(checkTouch);
  }, []);

  const modules = [Pagination, Mousewheel];
  
  if (autoplay) modules.push(Autoplay);
  if (scrollbar) modules.push(Scrollbar);
  if (effect === 'fade') modules.push(EffectFade);

  return (
    <div className={`swiper-gallery ${className}`}>
      <Swiper
        modules={modules}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        scrollbar={scrollbar ? { 
          hide: false,
          draggable: true,
          dragSize: 'auto'
        } : false}
        pagination={pagination ? { clickable: true } : false}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: true,
        }}
        autoplay={autoplay ? {
          delay: 3000,
          disableOnInteraction: false,
        } : false}
        effect={effect}
        breakpoints={{
          320: {
            slidesPerView: 'auto',
            spaceBetween: 20,
          },
          500: {
            slidesPerView: 'auto',
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 25,
          },
          950: {
            slidesPerView: 3,
            spaceBetween: spaceBetween,
          },
          1550: {
            slidesPerView: slidesPerView,
            spaceBetween: spaceBetween,
          },
        }}
        className="w-full"
      >
        {items.map((item) => (
          <SwiperSlide key={item.id}>
            {item.link ? (
              <Link href={item.link} target="_blank" className="block">
                <div className="relative group cursor-pointer">
                  <div className="aspect-[5/6] relative overflow-hidden rounded-3xl">
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      className="object-cover transition-transform duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] md:group-hover:scale-125 rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 md:group-hover:bg-opacity-30 transition-all duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)]" />
                  </div>

                  <div className="mt-5 mb-3">
                    <div className="eyebrow-lg mb-2">{item.date}</div>
                    <TruncatedTitle text={item.title} maxLines={3} />
                    <div className="flex items-center gap-1">
                      <span className="body-sm relative inline-block">
                        Read the article
                        <span className={`absolute bottom-0 left-0 h-[2px] bg-red-500 ${
                          isTouchDevice 
                            ? 'w-full' 
                            : 'w-0 transition-all duration-300 ease-out group-hover:w-full'
                        }`}></span>
                      </span>
                      <ArrowUpRight className="w-8 h-8 text-red-500" strokeWidth={1} />
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="relative group">
                <div className="aspect-[5/6] relative overflow-hidden rounded-3xl">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover transition-transform duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)] md:group-hover:scale-125 rounded-3xl"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 md:group-hover:bg-opacity-30 transition-all duration-[1000ms] ease-[cubic-bezier(0.19,1,0.22,1)]" />
                </div>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      <style jsx global>{`
        .swiper {
          overflow: visible;
        }

        .swiper:active {}

        .swiper-gallery {
          /* padding-bottom: 40px; */
        }

        .swiper-gallery .swiper-card-title-md {
          /* Prevent breaking words mid-word - only break at word boundaries */
          word-break: normal;
          overflow-wrap: break-word;
          hyphens: none;
        }

        .swiper-gallery .swiper-scrollbar {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 0.625rem;
          margin: 1.25rem auto 0 auto;
          height: 0.375rem;
          position: relative !important;
          /* width: 50% !important; */
          width: 80% !important;
          left: auto !important;
          transform: none !important;
          // at md: width: 50% !important;
          @media (min-width: 768px) {
            width: 50% !important;
            margin: 2.5rem auto 0 auto;
          }
        }

        .swiper-gallery .swiper-scrollbar-drag {
          background: #000000;
          border-radius: 0.625rem;
          height: 0.375rem !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          position: relative;
        }

        .swiper-gallery .swiper-scrollbar-drag::after {
          content: '';
          position: absolute;
          top: -0.96875rem;
          left: 0;
          right: 0;
          height: 2.5rem;
          background: transparent;
          z-index: 999;
          pointer-events: auto;
        }

        .swiper-gallery .swiper-scrollbar-drag:active {}

        .swiper-gallery .swiper-scrollbar-drag:hover {
          background: rgba(0, 0, 0, 0.7);
        }

        .swiper-gallery .swiper-pagination-bullet {
          background: #ffffff;
          opacity: 0.5;
        }

        .swiper-gallery .swiper-pagination-bullet-active {
          opacity: 1;
          background: #ffffff;
        }

        .swiper-gallery .swiper-pagination {
          bottom: 1.25rem;
        }
      `}</style>
    </div>
  );
}
